import { useRef, useState } from "react";
import { useTranslation } from "../../core/translation.core";
import PlusIcon from "../../icons/posicons/plus.icon";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FancyLabelAndInput } from "../reuseables/form.comp";
import { PosModal } from "../reuseables/modal.comp";
import { ReceiveRecycleFormData } from "../../types/recycle";
import RecycleProductSelector from "./recycleProductSelector.comp";
import recycleCore from "../../core/recycle.core";
import { toast } from "react-toastify";
import useRetailShop from "../../hooks/useRetailShop";
import { useRouter } from "next/router";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { formatCurrency } from "../../core/currency.core";
import { CustomerItem } from "../../types/customer";

export default function CustomerRecycles({ user }: { user: CustomerItem }) {
  const { t, msg } = useTranslation();
  const [showReceiveModal, setshowReceiveModal] = useState(false);

  return (
    <section className="space-y-10">
      <div className="flex justify-between items-center">
        <h1 className="font-bold">{t(msg.recycle.recycleRecord)}</h1>

        <button onClick={() => setshowReceiveModal(true)} className="bg-pos-blue text-white p-1 rounded-full">
          <PlusIcon />
        </button>
      </div>

      <RecycleInventoryList userId={user.id} />

      {showReceiveModal ? <ReceiveRecycleModal setShowReceiveModal={setshowReceiveModal} userId={user.id} /> : null}
    </section>
  );
}

const ReceiveRecycleModal = ({ setShowReceiveModal, userId }) => {
  const queryClient = useQueryClient();
  const { t, msg } = useTranslation();
  const receiveRecycleBtn = useRef(null);
  const pos = useRetailShop();
  const methods = useForm<ReceiveRecycleFormData>({
    defaultValues: { quantity: 1 },
  });

  const onSubmit = async (data: ReceiveRecycleFormData) => {
    recycleCore
      .receiveRecycle({ ...data, userId, warehouseId: pos?.warehouseIds?.[0]! })
      .then(() => {
        setShowReceiveModal(false);
        queryClient.invalidateQueries({ queryKey: ["inventory-log"] });
        toast.success(t(msg.order.success));
      })
      .catch((e) => {
        console.log(e);
        toast.error(t(msg.errors.contactAdmin));
      });
  };

  if (!pos) return null;
  return (
    //  @ts-ignore
    <PosModal setModalOpened={setShowReceiveModal} btnRef={receiveRecycleBtn}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="mx-8">
        <h1 className="text-2xl mb-8">{t(msg.recycle.recycle)}</h1>
        <div className="flex  mb-8 gap-3 items-end">
          <RecycleProductSelector methods={methods} />
          <div className="w-40">
            <FancyLabelAndInput
              type="number" //
              methods={methods}
              name="quantity"
              label=""
              step="1"
              min={1}
              placeholder={t(msg.formLabels.quantity)}
            />
          </div>
        </div>

        <button
          id="coupon-btn"
          className="flex-center w-full gap-1 bg-her-blue text-white py-3 mb-2 px-2 rounded-lg text-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
        >
          <span>{t(msg.form.collect)}</span>
        </button>
      </form>
    </PosModal>
  );
};

const RecycleInventoryList = ({ userId }: { userId: string }) => {
  const { isReady } = useRouter();
  const pos = useRetailShop();
  const { t, msg } = useTranslation();

  const {
    data: inventorylogs,
    isError,
    isPending,
    failureReason,
  } = useQuery({
    queryKey: ["inventory-log"],
    queryFn: (context) =>
      recycleCore.listRecycleLogs({
        userId,
        paging: {
          page: 1,
          size: 100,
        },
      }),
    enabled: isReady,
  });

  if (isError) return <ErrorCenterIcon reason={failureReason} />;
  if (isPending) return <LoadingOvalCenterIcon />;

  return (
    <section className="space-y-4 overflow-y-scroll">
      {inventorylogs?.list.map((i) => (
        <div key={i.id} className="grid grid-cols-[2fr_2fr_1fr_2fr] border-b-2 border-her-darkgray dark:border-white">
          <p className="truncate">{i.product.name}</p>
          <p className="text-center">{i.count > 0 ? t(msg.recycle.recycle) : t(msg.order.order)}</p>
          <p className={`text-end ${i.count < 0 ? "text-red-400" : ""}`}>{i.count}</p>
          <p className="flex justify-end">{formatCurrency(i.product.recycleDeposit, pos?.defaultCurrency ?? "HKD")}</p>
        </div>
      ))}
    </section>
  );
};
